// MUI Components
import { Box, Button, Skeleton, Theme, Typography, useTheme } from "@mui/material";

// Components
import { Body, Header } from "components/Layout/LayoutContent";
import StatusCard from "components/Pace/StatusCard";
import { StatusCardHeader } from "components/Pace/StatusCardHeader";
import CustomIcon from "components/CustomIcon";
import { LeadershipSnapshotCard } from "./components";
import { BehaviouralBreakdown } from "components/Charts";
import { BBCompletePaceError } from "components/Charts/BehaviouralBreakdown/ErrorMessages";
import YourProfile from "components/PaceLabsDashboard/YourProfile/YourProfile";
import { FormattedDate, FormattedMessage, useIntl } from "react-intl";

// Hooks
import useMyDashboard from "./useMyDashboard";

// Utils
import icons from "enums/icons";

// Types
import { RootState } from "app/store";

const MyDashboard = () => {
    const intl = useIntl();
    const theme = useTheme<Theme>();

    const {
        person_id,
        reportsData,
        reportStatus,
        chartData,
        statusLegend,
        statusBarColor,
        retakeAvailableOn,
        isRetakeAvailable,
        lastTaken,
        completedAssessments,
        hasOnGoingAssessment,
        hasCompletedAssessment,
        handleGoToMyExperience,
        handleGoToMyPace,
        handleStartPace,
        handleGoToReport,
    } = useMyDashboard();

    return (
        <>
            <Header backgroundColor="primary" sx={{ paddingTop: 3 }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
                    <Typography variant="h5" fontWeight={600}>
                        My Dashboard
                    </Typography>
                </Box>
            </Header>

            <Body sx={{ padding: 2 }}>
                <Box display="flex" alignItems="center">
                    {reportStatus === "fetching" && (
                        <Skeleton animation="wave" variant="rounded" sx={{ width: "100%", height: 225 }} />
                    )}
                    {reportStatus === "done" && (
                        <>
                            {hasCompletedAssessment && (
                                <StatusCard
                                    sx={{
                                        width: 306,
                                        minWidth: 266,
                                        height: 216,
                                        borderRadius: 4,
                                        overflow: "hidden",
                                        marginRight: theme.spacing(2),
                                    }}
                                    header={
                                        <StatusCardHeader
                                            title={statusLegend}
                                            icon={
                                                statusLegend === "Up To Date" ? icons.circledCheck : icons.errorTriangle
                                            }
                                            backgroundColor={statusBarColor}
                                        />
                                    }
                                    body={
                                        <Box
                                            sx={(theme: Theme) => ({
                                                height: 184,
                                                backgroundColor: theme.palette.gradients.g02.n1,
                                                color: theme.palette.common.white,
                                                padding: theme.spacing(2),
                                            })}
                                        >
                                            <Box display="flex" alignItems="center" mb={1}>
                                                <CustomIcon
                                                    icon={icons.paceIconOutlined}
                                                    sx={{ fontSize: 18, mr: 1 }}
                                                />
                                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                                    {intl.formatMessage({ id: "retestCard.title" })}
                                                </Typography>
                                            </Box>
                                            {lastTaken && (
                                                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                                                    {intl.formatMessage({ id: "retestCard.lastTaken" })}{" "}
                                                    <FormattedDate
                                                        value={new Date(lastTaken)}
                                                        month="short"
                                                        day="2-digit"
                                                        year="numeric"
                                                    />
                                                </Typography>
                                            )}
                                            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 2 }}>
                                                {intl.formatMessage({ id: "retestCard.numberOfAssessments" })}{" "}
                                                {completedAssessments}
                                            </Typography>

                                            <Box display="flex" justifyContent="center">
                                                {!isRetakeAvailable ? (
                                                    <Box display="flex" alignItems="center" mt={2}>
                                                        <CustomIcon
                                                            icon={icons.paceIconColouredDark}
                                                            sx={{ fontSize: 14, mr: 1 }}
                                                        />
                                                        <Typography
                                                            variant="body1"
                                                            fontSize={12}
                                                            letterSpacing="1.25px"
                                                        >
                                                            <FormattedMessage
                                                                id="retestCard.nextRetakeAvailable"
                                                                values={{ nextRetake: retakeAvailableOn }}
                                                            />
                                                        </Typography>
                                                    </Box>
                                                ) : (
                                                    <Button
                                                        variant="outlined"
                                                        size="small"
                                                        sx={{
                                                            height: 48,
                                                            color: "common.white",
                                                            borderColor: "common.white",
                                                            display: "flex",
                                                            "&:hover": { borderColor: "common.white" },
                                                        }}
                                                        onClick={handleStartPace}
                                                    >
                                                        <CustomIcon
                                                            icon={icons.paceIconColouredDark}
                                                            sx={{ fontSize: 14, mr: 1 }}
                                                        />
                                                        <Typography variant="body1" fontSize={12}>
                                                            {intl.formatMessage({ id: "retestCard.button.label" })}
                                                        </Typography>
                                                    </Button>
                                                )}
                                            </Box>
                                        </Box>
                                    }
                                />
                            )}

                            <Box
                                sx={(theme: Theme) => ({
                                    backgroundColor: theme.palette.background.paleBlue,
                                    borderRadius: "16px",
                                    padding: theme.spacing(2),
                                    display: "flex",
                                    flexDirection: "column",
                                    flex: 1,
                                })}
                            >
                                <Typography variant="subtitle1" color="secondary.main" mb={2}>
                                    {intl.formatMessage({ id: "pace.myDashboard.welcomeText" })}
                                </Typography>
                                <Box display="flex" flex={1}>
                                    <LeadershipSnapshotCard
                                        title={
                                            hasCompletedAssessment
                                                ? "pace.myDashboard.snapshotCard1.title"
                                                : "pace.myDashboard.snapshotCard4.title"
                                        }
                                        icon={
                                            <CustomIcon
                                                icon={icons.paceIconColouredDark}
                                                sx={{ fontSize: 21, mr: 1 }}
                                            />
                                        }
                                        backgroundColor={
                                            hasCompletedAssessment
                                                ? theme.palette.background.paper
                                                : theme.palette.primary.dark
                                        }
                                        buttonLabel={
                                            hasCompletedAssessment
                                                ? "View Assessment"
                                                : hasOnGoingAssessment
                                                ? "Continue Assessment"
                                                : "Start Assessment Now"
                                        }
                                        color={
                                            hasCompletedAssessment
                                                ? theme.palette.text.primary
                                                : theme.palette.common.white
                                        }
                                        sx={{
                                            button: !hasCompletedAssessment
                                                ? {
                                                      color: theme.palette.common.white,
                                                      borderColor: theme.palette.common.white,
                                                  }
                                                : undefined,
                                        }}
                                        onClick={hasCompletedAssessment ? handleGoToReport : handleStartPace}
                                    />
                                    <LeadershipSnapshotCard
                                        title={
                                            hasCompletedAssessment
                                                ? "pace.myDashboard.snapshotCard2.title"
                                                : "pace.myDashboard.snapshotCard3.title"
                                        }
                                        icon={
                                            <CustomIcon
                                                icon={hasCompletedAssessment ? icons.sparkles : icons.crossHair}
                                                sx={{ fontSize: 20, mr: 1 }}
                                            />
                                        }
                                        backgroundColor={theme.palette.background.paper}
                                        buttonLabel="Go To My Experience"
                                        onClick={handleGoToMyExperience}
                                    />
                                </Box>
                            </Box>
                        </>
                    )}
                </Box>

                {/* MAIN CONTAINER */}
                <Box display="flex" mt={2}>
                    <Box
                        width="65%"
                        onClick={hasCompletedAssessment ? handleGoToMyPace : undefined}
                        sx={{
                            mr: 2,
                            padding: theme.spacing(2, 4),
                            boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
                            borderRadius: "6px",
                            "&:hover": {
                                cursor: hasCompletedAssessment ? "pointer" : "default",
                            },
                        }}
                    >
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                            <Typography variant="h6" sx={{ fontWeight: 600, mt: 0, mb: 2 }}>
                                My Pace
                            </Typography>
                            <Box
                                sx={{
                                    width: 30,
                                    height: 30,
                                    fontSize: 12,
                                    fontWeight: 600,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    borderRadius: 10,
                                    color: theme.palette.common.white,
                                    backgroundColor: theme.palette.button.active.from,
                                }}
                            >
                                {reportsData?.user_assessments.length}
                            </Box>
                        </Box>

                        {reportStatus === "fetching" || chartData.status === "fetching" ? (
                            <Skeleton animation="wave" variant="rounded" sx={{ height: 430 }} />
                        ) : (
                            <Box>
                                <BehaviouralBreakdown
                                    width={0}
                                    height={0}
                                    getData={(store: RootState) => chartData.data}
                                    moreOptions={{
                                        shouldShowPaceDefinitions: false,
                                        hideCustomizeLabels: true,
                                        hideExpandButtons: true,
                                        shouldShowExpandAll: false,
                                        hideLegend: true,
                                        exporting: false,
                                        fallbackCondition: !hasCompletedAssessment,
                                        fallbackComponent: <BBCompletePaceError callBack={handleStartPace} />,
                                    }}
                                />
                                {hasCompletedAssessment && (
                                    <Box display="flex">
                                        <Button
                                            variant="outlined"
                                            sx={{
                                                flex: 1,
                                                width: "100%",
                                                maxWidth: 400,
                                                margin: "24px auto 8px",
                                                color: "text.primary",
                                            }}
                                            endIcon={<CustomIcon icon={icons.arrowRight} />}
                                            startIcon={
                                                <CustomIcon icon={icons.paceIconPrevious} sx={{ fontSize: 16 }} />
                                            }
                                            onClick={handleGoToMyPace}
                                        >
                                            {intl.formatMessage({ id: "pace.myDashboard.comparePace.button" })}
                                        </Button>
                                    </Box>
                                )}
                            </Box>
                        )}
                    </Box>
                    <Box width="35%">
                        <YourProfile
                            personId={person_id}
                            title={intl.formatMessage({ id: "pace.myDashboard.workHistory.title" })}
                            isEditable={false}
                        />
                    </Box>
                </Box>
            </Body>
        </>
    );
};

export default MyDashboard;
